import { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';
import { usePathname, useSearchParams } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';

import { InputText } from '@/components/form';
import { Combobox } from '@/components/ui/combobox';
import { caseApplicationsService, GET_CASES_STATUSES } from '@/api';
import { LabelState, Typography, TypographyType } from '@/components/common';
import Icons from '@/const/icons';
import clsx, { ClassValue } from 'clsx';
import { CaseStatuses } from '@/interfaces';

interface Props {
	sizeText?: TypographyType;
	classNameIcon?: string;
	classNameInput?: ClassValue;
	classNameCombo?: string;
	total: number | undefined;
	totalItems: number | undefined;
	paramLimit?: number;
}

export const Filters: React.FC<Props> = ({
	total,
	totalItems,
	sizeText = 'regular-16',
	classNameInput,
	classNameIcon,
	classNameCombo,
	paramLimit,
}) => {
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const { replace } = useRouter();
	const { register, setValue } = useForm({});

	const params = new URLSearchParams(searchParams);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [optionSelected, setOptionSelected] = useState<string | undefined>();

	useEffect(() => {
		const statusParam = searchParams.get('status') || undefined;
		setOptionSelected(statusParam);

		const searchParam = searchParams.get('search') || '';
		setValue('search', searchParam);
	}, [searchParams, setValue]);

	const { data: caseStatuses } = useQuery({
		queryKey: [GET_CASES_STATUSES],
		queryFn: () => caseApplicationsService.getCaseStatuses(),
	});

	const casesOptions =
		caseStatuses?.data
			.map(({ name }: CaseStatuses) => ({
				value: name,
				label: name,
				icon: <LabelState status={name} withLabel={false} />,
			}))
			.sort(
				(
					a: { value: string; label: string; icon: React.ReactNode },
					b: { value: string; label: string; icon: React.ReactNode }
				) => {
					const priorityOrder = [
						'View All',
						'Alphabetical Order',
						'In Progress',
						'Action Required',
						'On Hold',
						'Resolved Case',
						'Closed Case',
					];
					const aIndex = priorityOrder.indexOf(a.label);
					const bIndex = priorityOrder.indexOf(b.label);

					if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
					if (aIndex !== -1) return -1;
					if (bIndex !== -1) return 1;

					return a.label.localeCompare(b.label);
				}
			) || [];

	const handleChange = (id: string | undefined) => {
		setOptionSelected(id);
		if (id) {
			params.set('status', id);
		} else {
			params.delete('status');
		}
		replace(`${pathname}?${params.toString()}`);
	};

	const handleSearch = useDebouncedCallback((value: string) => {
		params.set('page', '1');
		if (value) {
			params.set('search', value);
		} else {
			params.delete('search');
		}
		replace(`${pathname}?${params.toString()}`);
	}, 300);

	return (
		<div className="flex justify-between items-baseline">
			<div className="flex flex-col xl:flex-row w-full items-baseline gap-4 flex-1">
				<InputText
					name="search"
					register={register}
					placeholder="Search"
					iconLeft={Icons.search}
					className="w-full 2xl:w-auto"
					classNameIcon={clsx('!top-[10%] !size-5', classNameIcon)}
					classNameInput={clsx(
						'pl-10 border-none xl:w-[417px] !-mt-2',
						classNameInput
					)}
					onChange={({ target }) => handleSearch(target.value)}
				/>
				<div className="flex items-center justify-between w-full max-h-44">
					<div>
						<Combobox
							open={isOpen}
							value={optionSelected}
							setOpen={(value) => setIsOpen(value)}
							placeholder="View All"
							options={casesOptions}
							setValue={(value) => handleChange(value)}
							classNameButton={classNameCombo}
							classNameCommand="w-[215px]"
						/>
					</div>
					<div className="block xl:hidden">
						<ShowingLabel
							sizeText={sizeText}
							total={total}
							totalItems={totalItems}
							paramSize={params.size}
							paramLimit={paramLimit}
						/>
					</div>
				</div>
			</div>
			<div className="flex-1 hidden xl:block">
				<ShowingLabel
					sizeText={sizeText}
					total={total}
					totalItems={totalItems}
					paramSize={params.size}
					paramLimit={paramLimit}
				/>
			</div>
		</div>
	);
};

interface ShowingLabelProps {
	sizeText: TypographyType;
	total?: number;
	totalItems?: number;
	paramSize: number;
	paramLimit?: number;
}

const ShowingLabel: React.FC<ShowingLabelProps> = ({
	sizeText,
	total,
	totalItems,
	paramSize,
	paramLimit = 3, // > 3 since "tab" "from" "page" will always exist
}) => {
	const totalRecords = useRef(null);

	useEffect(() => {
		if (totalRecords.current === null && totalItems !== undefined) {
			(totalRecords.current as number | null) = totalItems || 0;
		}
	}, [totalItems]);

	return (
		<Typography type={sizeText} className="opacity-60 text-right">
			{paramSize > paramLimit
				? `Showing ${totalItems || '0'} ${
						totalItems && totalItems === 1 ? 'result' : 'results'
				  } of ${totalRecords.current || '0'} ${
						totalRecords && totalRecords?.current && totalRecords?.current === 1
							? 'case'
							: 'cases'
				  }.`
				: `Showing ${total || '0'} of ${totalItems || '0'} ${
						totalItems && totalItems === 1 ? 'case' : 'cases'
				  }`}
		</Typography>
	);
};
